/**
 * PlatformSettingsTab - Main tab component for Platform Settings
 * Contains accordion layout for all settings categories with company scope selection
 */
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { CompanyScopeSelector } from "./components/company-scope-selector";
import { CategoryAccordion } from "./components/category-accordion";
import { useSettings, useUpdateCategorySettings } from "@/hooks/api/use-settings";
import { CATEGORY_ORDER } from "./category-config";
import { toast } from "@/lib/toast-service";
import type { SettingCategory } from "@/types/api/settings";

export function PlatformSettingsTab() {
    const [searchParams, setSearchParams] = useSearchParams();
    
    // State for company scope selection
    const [selectedScope, setSelectedScope] = useState("global");

    // Parse expanded categories from URL or default to scheduler
    const parseCategoriesFromUrl = useCallback((params: URLSearchParams): Set<SettingCategory> => {
        const cardsParam = params.get("cards");
        if (cardsParam) {
            const categories = cardsParam.split(",").filter((cat): cat is SettingCategory => 
                CATEGORY_ORDER.includes(cat as SettingCategory)
            );
            return new Set(categories);
        }
        return new Set(["scheduler"] as SettingCategory[]);
    }, []);

    // State for expanded accordion sections - initialize from URL
    const [expandedCategories, setExpandedCategories] = useState<Set<SettingCategory>>(() => 
        parseCategoriesFromUrl(searchParams)
    );

    // Ref to track if update is from URL (to avoid infinite loops)
    const isUpdatingFromUrl = useRef(false);

    // State for saving status per category
    const [savingCategory, setSavingCategory] = useState<SettingCategory | null>(null);

    // Fetch settings
    const { data: settingsData, isLoading, error, refetch } = useSettings(selectedScope);

    // Mutation for bulk update
    const updateCategorySettings = useUpdateCategorySettings();

    // Sync URL when expanded categories change (but not when updating from URL)
    useEffect(() => {
        if (isUpdatingFromUrl.current) {
            isUpdatingFromUrl.current = false;
            return;
        }

        const categoriesArray = Array.from(expandedCategories).sort();
        const currentCardsParam = searchParams.get("cards");
        const currentCategories = currentCardsParam 
            ? currentCardsParam.split(",").sort() 
            : [];
        const newCardsParam = categoriesArray.length > 0 ? categoriesArray.join(",") : null;
        
        // Only update URL if it's different
        if (JSON.stringify(categoriesArray) !== JSON.stringify(currentCategories)) {
            const newParams = new URLSearchParams(searchParams);
            if (newCardsParam) {
                newParams.set("cards", newCardsParam);
            } else {
                newParams.delete("cards");
            }
            setSearchParams(newParams, { replace: true });
        }
    }, [expandedCategories, searchParams, setSearchParams]);

    // Sync state when URL changes (e.g., browser back/forward)
    useEffect(() => {
        const urlCategories = parseCategoriesFromUrl(searchParams);
        const urlCategoriesArray = Array.from(urlCategories).sort();
        
        // Use a ref to get current state without including it in dependencies
        setExpandedCategories((prev) => {
            const currentCategoriesArray = Array.from(prev).sort();
            
            // Only update state if URL categories are different
            if (JSON.stringify(currentCategoriesArray) !== JSON.stringify(urlCategoriesArray)) {
                isUpdatingFromUrl.current = true;
                return urlCategories;
            }
            return prev;
        });
    }, [searchParams, parseCategoriesFromUrl]);

    // Toggle accordion section
    const handleToggleCategory = useCallback((category: SettingCategory) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    }, []);

    // Save category settings
    const handleSaveCategory = useCallback(
        async (category: SettingCategory, values: Record<string, unknown>) => {
            setSavingCategory(category);
            try {
                await updateCategorySettings.mutateAsync({
                    category,
                    settings: values,
                    companyId: selectedScope !== "global" ? selectedScope : undefined,
                });
                toast.success(`${category} settings saved successfully`);
            } catch (error) {
                console.error("Failed to save settings:", error);
                toast.error(`Failed to save ${category} settings`);
            } finally {
                setSavingCategory(null);
            }
        },
        [updateCategorySettings, selectedScope]
    );

    // Reset category (refetch data)
    const handleResetCategory = useCallback(() => {
        refetch();
    }, [refetch]);

    // Get settings for each category (API returns { category: Setting[] } directly)
    const categorizedSettings = useMemo(() => {
        if (!settingsData) return {};
        return settingsData;
    }, [settingsData]);

    // Loading state
    if (isLoading) {
        return (
            <div className="space-y-6">
                <CompanyScopeSelector selectedScope={selectedScope} onScopeChange={setSelectedScope} />
                <div className="flex items-center justify-center py-12 text-tertiary">Loading settings...</div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="space-y-6">
                <CompanyScopeSelector selectedScope={selectedScope} onScopeChange={setSelectedScope} />
                <div className="rounded-lg border border-error-primary/20 bg-error-primary/5 p-6">
                    <p className="text-error-primary font-medium">Failed to load settings</p>
                    <p className="text-sm text-tertiary mt-1">Please try refreshing the page or contact support.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Company Scope Selector */}
            <CompanyScopeSelector selectedScope={selectedScope} onScopeChange={setSelectedScope} />

            {/* Settings Categories */}
            <div>
                {CATEGORY_ORDER.map((category) => {
                    // API returns settings directly as array per category
                    const settings = categorizedSettings[category] || [];

                    return (
                        <CategoryAccordion
                            key={category}
                            category={category}
                            settings={settings}
                            isExpanded={expandedCategories.has(category)}
                            onToggle={() => handleToggleCategory(category)}
                            onSave={(values) => handleSaveCategory(category, values)}
                            onReset={handleResetCategory}
                            isSaving={savingCategory === category}
                        />
                    );
                })}
            </div>

            {/* Empty state if no categories */}
            {Object.keys(categorizedSettings).length === 0 && (
                <div className="rounded-lg border border-secondary bg-secondary/50 p-8 text-center">
                    <p className="text-tertiary">No settings available</p>
                    <p className="text-sm text-tertiary mt-1">Settings will appear here once they are configured.</p>
                </div>
            )}
        </div>
    );
}
